/**
 * P2e (2026-07-05) — Warehouse (Magazyn) Dashboard.
 *
 * User wymaganie: „magazyn ma widzieć TYLKO 4 kafelki — Zamówienia,
 * Checklista, Raport produkcji, Produkcja dziś — bez Rozliczenia dnia".
 * Dodatkowo i18n TH/PL/RU (magazyn to często tajska/rosyjska ekipa).
 *
 * Wzorzec i18n analogiczny do KitchenOrder.js (obiekty pl/th/ru + selektor).
 */
import React from "react";
import { useAuth } from "@/lib/auth-context";
import { useT } from "@/lib/i18n-context";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ListChecks, ClipboardCheck, Soup, ClipboardList, LogOut } from "lucide-react";

const I18N = {
  pl: {
    hello: "Cześć",
    subtitle: "Panel magazynu — zamówienia, checklisty, produkcja.",
    logout: "Wyloguj",
    tiles: {
      orders:     { title: "Zamówienia",       desc: "Przygotuj koszyk i wyślij do dostawcy." },
      checklist:  { title: "Checklista zmiany", desc: "Uzupełnij listę zadań na start/koniec zmiany." },
      report:     { title: "Raport produkcji",  desc: "Zgłoś ile wyprodukowano — paczki / kg (krewetki)." },
      todo:       { title: "Produkcja — dziś",  desc: "Lista dań do przygotowania na dziś." },
    },
  },
  th: {
    hello: "สวัสดี",
    subtitle: "แผงควบคุมคลังสินค้า — คำสั่งซื้อ เช็คลิสต์ การผลิต",
    logout: "ออกจากระบบ",
    tiles: {
      orders:     { title: "คำสั่งซื้อ",           desc: "เตรียมตะกร้าและส่งให้ผู้จัดจำหน่าย" },
      checklist:  { title: "เช็คลิสต์กะ",          desc: "กรอกรายการเริ่ม/จบกะ" },
      report:     { title: "รายงานการผลิต",       desc: "แจ้งจำนวนที่ผลิต — แพ็ค / กก. (กุ้ง)" },
      todo:       { title: "การผลิต — วันนี้",     desc: "รายการอาหารที่ต้องเตรียมวันนี้" },
    },
  },
  ru: {
    hello: "Привет",
    subtitle: "Панель склада — заказы, чек-листы, производство.",
    logout: "Выход",
    tiles: {
      orders:     { title: "Заказы",              desc: "Соберите корзину и отправьте поставщику." },
      checklist:  { title: "Чек-лист смены",       desc: "Заполните список задач начала/конца смены." },
      report:     { title: "Отчёт производства",   desc: "Укажите сколько произведено — пачки / кг (креветки)." },
      todo:       { title: "Производство — сегодня", desc: "Список блюд на приготовление сегодня." },
    },
  },
};

const TILES = [
  { key: "orders",    to: "/kitchen",             Icon: ListChecks,      color: "text-[var(--tt-primary)]"     },
  { key: "checklist", to: "/shift-checklists",    Icon: ClipboardCheck,  color: "text-[var(--tt-info-fg)]"     },
  { key: "report",    to: "/production/report",   Icon: ClipboardList,   color: "text-[var(--tt-success-fg)]"  },
  { key: "todo",      to: "/production/overview", Icon: Soup,            color: "text-[var(--tt-warn-fg)]"     },
];

export default function WarehouseDashboard() {
  const { user, logout } = useAuth();
  // P2g (2026-07-05) — używamy GLOBALNEGO useT() zamiast lokalnego stanu,
  // dzięki czemu selektor języka w Sidebar synchronizuje ten dashboard.
  const { lang } = useT();
  const tt = (I18N[lang] || I18N.pl);

  return (
    <div className="space-y-6" data-testid="warehouse-dashboard">
      {/* Header: powitanie + logout (selektor języka jest w Sidebar globalnie) */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tt-display">
            {tt.hello}, {user?.full_name?.split(" ")[0] || "Magazyn"}!
          </h1>
          <p className="text-[var(--tt-muted)] mt-1">{tt.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => logout()} data-testid="warehouse-logout-button" className="gap-1">
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">{tt.logout}</span>
          </Button>
        </div>
      </div>

      {/* 4 kafelki (tylko te 4 — bez Rozliczenia dnia zgodnie z wymogiem user) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="warehouse-tile-grid">
        {TILES.map(({ key, to, Icon, color }) => (
          <Link key={key} to={to} className="block" data-testid={`warehouse-tile-${key}`}>
            <Card className="tt-card hover:border-[var(--tt-primary)] transition-colors h-full">
              <CardContent className="p-5 flex flex-col items-start gap-3">
                <div className={`h-14 w-14 rounded-xl bg-[var(--tt-primary)]/10 ${color} flex items-center justify-center`}>
                  <Icon className="h-7 w-7" />
                </div>
                <div className="font-semibold tt-display text-lg">{tt.tiles[key].title}</div>
                <div className="text-sm text-[var(--tt-muted)]">{tt.tiles[key].desc}</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
